import React, { useContext, useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';
import { SnackbarContext } from '../SnackbarContext';

function generateRandomCode(length = 12) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  '& .MuiTableSortLabel-root': {
    color: `${theme.palette.common.white} !important`,
    '&.Mui-active': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiTableSortLabel-icon': {
      color: `${theme.palette.common.white} !important`,
    },
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.75) !important',
    },
  },
}));

export default function CodeGen() {
  const { userData } = useContext(AppContext);

  // Hooks
  const [codeCount, setCodeCount] = useState(1);
  const [duration, setDuration] = useState(30);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unusedCodes, setUnusedCodes] = useState([]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  const { showSuccess, showError } = useContext(SnackbarContext);

  // Real-time listener for codes
  useEffect(() => {
    const codesRef = collection(db, 'accessCodes');
    const q = query(codesRef, where('redeemed', '==', false));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const codeList = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setUnusedCodes(codeList);
      },
      (err) => {
        showError('Failed to load codes.');
      }
    );

    return () => unsubscribe();
  }, []);

  // Sorting logic
  const sortedCodes = useMemo(() => {
    const sorted = [...unusedCodes].sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'createdAt') {
        aVal = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        bVal = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
      } else {
        aVal = a[sortColumn];
        bVal = b[sortColumn];
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [unusedCodes, sortColumn, sortDirection]);

  // Developer check
  if (!userData?.isDeveloper) {
    return <Typography>You do not have permission to view this page.</Typography>;
  }

  // Generate codes
  const createCodes = async (count) => {
    setError('');
    setSuccess('');
    try {
      for (let i = 0; i < count; i++) {
        const code = generateRandomCode();
        await setDoc(doc(db, 'accessCodes', code), {
          accessDuration: duration,
          createdAt: new Date(),
          redeemed: false,
        });
      }
      showSuccess(`${count} code(s) created!`);
    } catch (err) {
      console.error('Error creating codes:', err);
      showError('Failed to create codes.');
    }
  };

  // Copy code
  const handleCopy = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        showSuccess(`Code copied to clipboard!`);
      })
      .catch((err) => {
        showError('Failed to copy code.');
      });
  };

  // Delete code
  const handleDelete = async (code) => {
    try {
      await deleteDoc(doc(db, 'accessCodes', code));
      showSuccess(`Code "${code}" deleted!`);
    } catch (err) {
      showError('Failed to delete code.');
    }
  };

  // Toggle sorting
  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Typography variant="h4" gutterBottom>
        Code Generator
      </Typography>

      {/* Generate Codes Table */}
      <TableContainer component={Paper}>
        <Table
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              border: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiTableCell-head, & .MuiTableCell-body:first-of-type': {
              fontWeight: 'bold',
            },
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: '40%' }}>
                <FormControl fullWidth>
                  <InputLabel id="codeCount-label">Number of Codes</InputLabel>
                  <Select
                    labelId="codeCount-label"
                    value={codeCount}
                    label="Number of Codes"
                    onChange={(e) => setCodeCount(e.target.value)}
                  >
                    <MenuItem value={1}>1</MenuItem>
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell  sx={{ width: '40%' }}>
                <FormControl fullWidth>
                  <InputLabel id="duration-label">Duration (Days)</InputLabel>
                  <Select
                    labelId="duration-label"
                    value={duration}
                    label="Duration (Days)"
                    onChange={(e) => setDuration(e.target.value)}
                  >
                    <MenuItem value={30}>30 Days</MenuItem>
                    <MenuItem value={90}>90 Days</MenuItem>
                    <MenuItem value={180}>180 Days</MenuItem>
                    <MenuItem value={360}>360 Days</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={{ width: '20%' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => createCodes(codeCount)}
                >
                  Generate Codes
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ my: 2 }} />

      {/* Unused Codes Table */}
      <TableContainer component={Paper}>
        <Table
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              border: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiTableCell-head, & .MuiTableCell-body:first-of-type': {
              fontWeight: 'bold',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: '35%' }}>
                <TableSortLabel
                  active={sortColumn === 'id'}
                  direction={sortDirection}
                  onClick={() => handleSort('id')}
                >
                  Code
                </TableSortLabel>
              </StyledTableCell>

              <StyledTableCell sx={{ width: '30%' }}>
                <TableSortLabel
                  active={sortColumn === 'createdAt'}
                  direction={sortDirection}
                  onClick={() => handleSort('createdAt')}
                >
                  Date Created
                </TableSortLabel>
              </StyledTableCell>

              <StyledTableCell sx={{ width: '15%' }}>
                <TableSortLabel
                  active={sortColumn === 'accessDuration'}
                  direction={sortDirection}
                  onClick={() => handleSort('accessDuration')}
                >
                  Time (Days)
                </TableSortLabel>
              </StyledTableCell>

              <StyledTableCell sx={{ width: '10%' }}>
                Copy
              </StyledTableCell>

              <StyledTableCell sx={{ width: '10%' }}>
                Delete
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCodes.map((codeDoc) => {
              let createdAtString = '';
              if (codeDoc.createdAt?.toDate) {
                createdAtString = codeDoc.createdAt.toDate().toLocaleString();
              }

              return (
                <TableRow key={codeDoc.id}>
                  <TableCell>{codeDoc.id}</TableCell>
                  <TableCell>{createdAtString}</TableCell>
                  <TableCell>{codeDoc.accessDuration}</TableCell>

                  {/* Copy Button */}
                  <TableCell>
                    <IconButton color="secondary" onClick={() => handleCopy(codeDoc.id)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </TableCell>

                  {/* Delete Button */}
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDelete(codeDoc.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Error Snackbar */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}


